import { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Trash2,
  MessageCircle,
  Phone,
  Loader,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import WhatsAppContactModal from './WhatsAppContactModal';
import ManagementDeleteModal from './ManagementDeleteModal';
import { getWhatsAppContacts, saveWhatsAppContacts } from './../services/managementService';

function formatPhoneForDisplay(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '');

  if (digits.startsWith('55') && digits.length >= 12) {
    const ddd = digits.slice(2, 4);
    const part1 = digits.slice(4, 9);
    const part2 = digits.slice(9);
    return `+55 ${ddd} ${part1}-${part2}`;
  }

  if (digits.length > 6) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}-${digits.slice(8)}`;
  }

  return `+${digits}`;
}

export default function WhatsAppContactsTab() {
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [phoneToDelete, setPhoneToDelete] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getWhatsAppContacts();
      setPhoneNumbers(data);
    } catch (err) {
      setError('Erro ao carregar contatos. Tente novamente.');
      console.error('Error fetching WhatsApp contacts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredPhones = phoneNumbers.filter((phone) =>
    formatPhoneForDisplay(phone).includes(searchTerm) || phone.includes(searchTerm.replace(/\D/g, ''))
  );

  const handleAdd = () => {
    setIsModalOpen(true);
  };

  const handleDelete = (phone: string) => {
    setPhoneToDelete(phone);
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (data: { phoneNumber: string }) => {
    setIsSubmitting(true);
    try {
      const newPhoneNumbers = [...phoneNumbers, data.phoneNumber];
      await saveWhatsAppContacts(newPhoneNumbers);
      setPhoneNumbers(newPhoneNumbers);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving WhatsApp contact:', err);
      setError('Erro ao salvar contato. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!phoneToDelete) return;

    setIsSubmitting(true);
    try {
      const newPhoneNumbers = phoneNumbers.filter((p) => p !== phoneToDelete);
      await saveWhatsAppContacts(newPhoneNumbers);
      setPhoneNumbers(newPhoneNumbers);
      setIsDeleteModalOpen(false);
      setPhoneToDelete(undefined);
    } catch (err) {
      console.error('Error deleting WhatsApp contact:', err);
      setError('Erro ao excluir contato. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-green-600 mb-4" />
        <p className="text-gray-600">Carregando contatos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Alertas - WhatsApp
            </h2>
            <p className="text-sm text-gray-500">
              Gerencie os números que receberão alertas pelo WhatsApp
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={fetchData}
            className="text-red-600 hover:text-red-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Atualizar"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              <span>Novo Número</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredPhones.length === 0 ? (
          <div className="p-12 text-center">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum numero encontrado' : 'Nenhum numero cadastrado'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? 'Tente buscar com outros termos'
                : 'Adicione números que irão receber alertas pelo WhatsApp'}
            </p>
            {!searchTerm && (
              <button
                onClick={handleAdd}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Numero</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Número WhatsApp
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPhones.map((phone, index) => (
                    <tr key={`${phone}-${index}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Phone className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {formatPhoneForDisplay(phone)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDelete(phone)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-gray-200">
              {filteredPhones.map((phone, index) => (
                <div key={`${phone}-${index}`} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="font-medium text-gray-900">
                        {formatPhoneForDisplay(phone)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(phone)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <p className="text-sm text-gray-500 text-center">
        {filteredPhones.length} {filteredPhones.length === 1 ? 'número' : 'números'} cadastrado{filteredPhones.length !== 1 ? 's' : ''}
      </p>

      <WhatsAppContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isSubmitting={isSubmitting}
      />

      <ManagementDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPhoneToDelete(undefined);
        }}
        onConfirm={handleConfirmDelete}
        itemName={phoneToDelete ? formatPhoneForDisplay(phoneToDelete) : ''}
        itemType="contato"
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
